/**
 * Theme Preview Page
 *
 * Visual reference for the FixMyBrick design system color tokens.
 * Use this page to verify color consistency and contrast ratios.
 *
 * Access at: /design/theme-preview (only visible in development)
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Sun,
  Moon,
  Palette
} from 'lucide-react';

/**
 * Color swatch component for displaying individual colors
 */
function ColorSwatch({
  name,
  variable,
  className
}: {
  name: string;
  variable: string;
  className: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div
        className={`h-16 w-full rounded-lg border border-border shadow-sm ${className}`}
        title={variable}
      />
      <span className="text-xs font-medium text-foreground">{name}</span>
      <span className="text-2xs text-muted-foreground font-mono">{variable}</span>
    </div>
  );
}

/**
 * Section header component
 */
function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold text-foreground tracking-tight">{title}</h2>
      {description && (
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      )}
    </div>
  );
}

export default function ThemePreview() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <Palette className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Theme Preview</h1>
              <p className="text-sm text-muted-foreground">FixMyBrick Design System</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 space-y-12">

        {/* ════════════════════════════════════════════════════════════════════
            PRIMARY COLOR SCALE
            ════════════════════════════════════════════════════════════════════ */}
        <section>
          <SectionHeader
            title="Primary Scale"
            description="Brand red with hover, active, and background variations"
          />
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-3">
            <ColorSwatch name="50" variable="--primary-50" className="bg-primary-50" />
            <ColorSwatch name="100" variable="--primary-100" className="bg-primary-100" />
            <ColorSwatch name="200" variable="--primary-200" className="bg-primary-200" />
            <ColorSwatch name="300" variable="--primary-300" className="bg-primary-300" />
            <ColorSwatch name="400" variable="--primary-400" className="bg-primary-400" />
            <ColorSwatch name="500" variable="--primary-500" className="bg-primary-500" />
            <ColorSwatch name="600" variable="--primary-600" className="bg-primary-600" />
            <ColorSwatch name="700" variable="--primary-700" className="bg-primary-700" />
            <ColorSwatch name="800" variable="--primary-800" className="bg-primary-800" />
            <ColorSwatch name="900" variable="--primary-900" className="bg-primary-900" />
          </div>

          {/* Usage examples */}
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium text-foreground mb-3">Usage Examples</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-primary-50 text-primary-900 rounded text-sm">
                bg-primary-50 (subtle bg)
              </span>
              <span className="px-3 py-1 bg-primary-100 text-primary-900 rounded text-sm">
                bg-primary-100 (hover bg)
              </span>
              <span className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm">
                bg-primary (buttons)
              </span>
              <span className="px-3 py-1 bg-primary-600 text-primary-foreground rounded text-sm">
                bg-primary-600 (hover)
              </span>
              <span className="px-3 py-1 bg-primary-700 text-primary-foreground rounded text-sm">
                bg-primary-700 (active)
              </span>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            STATUS COLORS
            ════════════════════════════════════════════════════════════════════ */}
        <section>
          <SectionHeader
            title="Status Colors"
            description="Semantic colors for feedback states"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Success */}
            <Card className="border-success/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  <CardTitle className="text-sm">Success</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-10 bg-success rounded" />
                <div className="h-10 bg-success/10 rounded border border-success/20" />
                <Badge variant="success">Success Badge</Badge>
              </CardContent>
            </Card>

            {/* Warning */}
            <Card className="border-warning/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <CardTitle className="text-sm">Warning</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-10 bg-warning rounded" />
                <div className="h-10 bg-warning/10 rounded border border-warning/20" />
                <Badge variant="warning">Warning Badge</Badge>
              </CardContent>
            </Card>

            {/* Destructive */}
            <Card className="border-destructive/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <CardTitle className="text-sm">Destructive</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-10 bg-destructive rounded" />
                <div className="h-10 bg-destructive/10 rounded border border-destructive/20" />
                <Badge variant="destructive">Error Badge</Badge>
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="border-info/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-info" />
                  <CardTitle className="text-sm">Info</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-10 bg-info rounded" />
                <div className="h-10 bg-info/10 rounded border border-info/20" />
                <Badge variant="info">Info Badge</Badge>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            NEUTRAL SURFACES
            ════════════════════════════════════════════════════════════════════ */}
        <section>
          <SectionHeader
            title="Neutral Surfaces"
            description="Subtly warm neutrals that complement the brand red"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="h-24 bg-background rounded-lg border border-border" />
              <p className="text-sm font-medium">Background</p>
              <p className="text-2xs text-muted-foreground">Main page background</p>
            </div>
            <div className="space-y-2">
              <div className="h-24 bg-card rounded-lg border border-border" />
              <p className="text-sm font-medium">Card</p>
              <p className="text-2xs text-muted-foreground">Elevated surface</p>
            </div>
            <div className="space-y-2">
              <div className="h-24 bg-muted rounded-lg border border-border" />
              <p className="text-sm font-medium">Muted</p>
              <p className="text-2xs text-muted-foreground">Secondary surface</p>
            </div>
            <div className="space-y-2">
              <div className="h-24 bg-secondary rounded-lg border border-border" />
              <p className="text-sm font-medium">Secondary</p>
              <p className="text-2xs text-muted-foreground">Brand secondary</p>
            </div>
          </div>

          {/* Text on surfaces */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Text Hierarchy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-foreground">Foreground - Primary text</p>
                <p className="text-muted-foreground">Muted Foreground - Secondary text</p>
                <p className="text-primary">Primary - Brand emphasis</p>
                <p className="text-accent">Accent - Neutral accent</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Border & Input</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-10 border-2 border-border rounded" />
                <Input placeholder="Input field example" />
                <div className="h-10 border-2 border-input rounded ring-2 ring-ring ring-offset-2" />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            CHART PALETTE
            ════════════════════════════════════════════════════════════════════ */}
        <section>
          <SectionHeader
            title="Chart Palette"
            description="Cohesive colors for data visualization"
          />
          <div className="grid grid-cols-5 gap-3">
            <ColorSwatch name="Chart 1" variable="--chart-1" className="bg-chart-1" />
            <ColorSwatch name="Chart 2" variable="--chart-2" className="bg-chart-2" />
            <ColorSwatch name="Chart 3" variable="--chart-3" className="bg-chart-3" />
            <ColorSwatch name="Chart 4" variable="--chart-4" className="bg-chart-4" />
            <ColorSwatch name="Chart 5" variable="--chart-5" className="bg-chart-5" />
          </div>

          {/* Simulated bar chart */}
          <div className="mt-6 p-4 bg-card rounded-lg border border-border">
            <p className="text-sm font-medium text-foreground mb-4">Sample Bar Chart</p>
            <div className="flex items-end gap-2 h-32">
              <div className="flex-1 bg-chart-1 rounded-t" style={{ height: '85%' }} />
              <div className="flex-1 bg-chart-2 rounded-t" style={{ height: '65%' }} />
              <div className="flex-1 bg-chart-3 rounded-t" style={{ height: '90%' }} />
              <div className="flex-1 bg-chart-4 rounded-t" style={{ height: '45%' }} />
              <div className="flex-1 bg-chart-5 rounded-t" style={{ height: '70%' }} />
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            COMPONENT EXAMPLES
            ════════════════════════════════════════════════════════════════════ */}
        <section>
          <SectionHeader
            title="Component Examples"
            description="Common UI patterns using the design system"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Buttons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Button Variants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button>Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="destructive">Destructive</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm">Small</Button>
                  <Button size="default">Default</Button>
                  <Button size="lg">Large</Button>
                </div>
              </CardContent>
            </Card>

            {/* Badges */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Badge Variants</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge>Default</Badge>
                  <Badge variant="secondary">Secondary</Badge>
                  <Badge variant="outline">Outline</Badge>
                  <Badge variant="success">Success</Badge>
                  <Badge variant="warning">Warning</Badge>
                  <Badge variant="destructive">Destructive</Badge>
                  <Badge variant="info">Info</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Focus States */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Focus States</CardTitle>
                <CardDescription>Tab through to see focus rings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input placeholder="Focus me (Tab)" />
                <Button>Focus me</Button>
                <Button variant="outline">Outline Focus</Button>
              </CardContent>
            </Card>

            {/* Theme Toggle Example */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Theme Modes</CardTitle>
                <CardDescription>Test both light and dark modes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2 p-3 bg-white text-gray-900 rounded-lg border">
                    <Sun className="h-4 w-4" />
                    <span className="text-sm">Light</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-900 text-white rounded-lg">
                    <Moon className="h-4 w-4" />
                    <span className="text-sm">Dark</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            CONTRAST CHECK
            ════════════════════════════════════════════════════════════════════ */}
        <section>
          <SectionHeader
            title="Contrast Check"
            description="Visual verification of text contrast on backgrounds"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-background rounded-lg border border-border">
              <p className="text-foreground font-medium">Foreground on Background</p>
              <p className="text-muted-foreground text-sm">Muted text should be readable</p>
            </div>
            <div className="p-4 bg-primary rounded-lg">
              <p className="text-primary-foreground font-medium">White on Primary</p>
              <p className="text-primary-foreground/80 text-sm">Should meet WCAG AA</p>
            </div>
            <div className="p-4 bg-card rounded-lg border border-border">
              <p className="text-card-foreground font-medium">Text on Card</p>
              <p className="text-muted-foreground text-sm">Card surfaces</p>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/50 mt-12">
        <div className="container mx-auto px-6 py-4">
          <p className="text-sm text-muted-foreground text-center">
            FixMyBrick Design System • Theme Preview • Development Only
          </p>
        </div>
      </footer>
    </div>
  );
}
